import React from "react";
import Chart from "../../components/chart";
import ServiceFactory from "../../service";
import { parseCustomRange } from "../sales/territory-common";

/*
 * Audience day-series trend (AUDIENCE_STATS_UX §2.B/2.C) —
 * POST /v1/audience/trends/{range} body = audience NAMES → dated rows
 * [{id, name, date, sessions, uniqueUsers, conversions, ...}] (fixed in
 * Service.Activity 1.0.29; $groupBy optional, defaults to name).
 *
 * ⚠ WINDOW GUARD: the deployed endpoint currently computes the PRIOR-YEAR
 * window (it ranges via DateTimeRangeComparison — this_week returns last
 * year's week; flagged to platform). Rows outside the range's REAL window
 * are dropped here, so the chart shows an honest empty state today and
 * lights up automatically when the window fix deploys. Do not remove the
 * guard — it's also what keeps backfilled history out of short ranges.
 */

export function rangeWindow(range) {
  const custom = parseCustomRange(range);
  if (custom) {
    const end = new Date(custom[1]);
    end.setDate(end.getDate() + 1); // exclusive end
    return [custom[0], end];
  }
  const now = new Date();
  const day = d => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = day(now);
  const addDays = (d, n) => {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
  };
  const monday = d => addDays(d, -((d.getDay() + 6) % 7));
  switch (range) {
    case "this_week":
      return [monday(today), addDays(monday(today), 7)];
    case "last_week":
      return [addDays(monday(today), -7), monday(today)];
    case "last_7_days":
      return [addDays(today, -7), today];
    case "this_month":
      return [new Date(now.getFullYear(), now.getMonth(), 1), new Date(now.getFullYear(), now.getMonth() + 1, 1)];
    case "last_month":
      return [new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 1)];
    case "this_quarter":
      {
        const q = Math.floor(now.getMonth() / 3);
        return [new Date(now.getFullYear(), q * 3, 1), new Date(now.getFullYear(), q * 3 + 3, 1)];
      }
    case "last_quarter":
      {
        const q = Math.floor(now.getMonth() / 3);
        return [new Date(now.getFullYear(), q * 3 - 3, 1), new Date(now.getFullYear(), q * 3, 1)];
      }
    case "this_year":
      return [new Date(now.getFullYear(), 0, 1), new Date(now.getFullYear() + 1, 0, 1)];
    case "last_year":
      return [new Date(now.getFullYear() - 1, 0, 1), new Date(now.getFullYear(), 0, 1)];
    default:
      return null;
    // today/yesterday/all_time → no day chart
  }
}
export async function fetchTrends(range, names) {
  const api = `/audience/trends/${range}`;
  const Service = ServiceFactory.getType(api, "product");
  const rows = await new Service().postAsync(names);
  const win = rangeWindow(range);
  if (!Array.isArray(rows)) return [];
  if (!win) return rows;
  return rows.filter(r => {
    const d = new Date(String(r.date).slice(0, 10) + "T00:00:00");
    return d >= win[0] && d < win[1];
  });
}
const PALETTE = ["#ff6358", "#2f80ed", "#14b8a6", "#a855f7"];

/*
 * rows     dated rows (already window-filtered by fetchTrends)
 * series   [{ name (audience name or null=all rows), metric, label }]
 *          — one line per entry; colors from PALETTE in order
 */
export default function AudienceTrendChart({
  rows,
  series,
  height = 220
}) {
  if (!rows || rows.length === 0) return null;
  const dates = [...new Set(rows.map(r => String(r.date).slice(0, 10)))].sort();
  if (dates.length < 2) return null;
  const lines = series.map((s, i) => {
    const byDate = {};
    rows.filter(r => !s.name || r.name === s.name).forEach(r => {
      byDate[String(r.date).slice(0, 10)] = Number(r[s.metric]) || 0;
    });
    return {
      ...s,
      color: PALETTE[i % PALETTE.length],
      points: dates.map(d => byDate[d] || 0)
    };
  }).filter(l => l.points.some(v => v > 0));
  if (lines.length === 0) return null;
  const label = d => {
    const t = new Date(d + "T00:00:00");
    return isNaN(t) ? d : t.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric"
    });
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "aud-trend"
  }, /*#__PURE__*/React.createElement(Chart, {
    width: "100%",
    height: `${height}px`,
    chartType: "LineChart",
    data: [["Day", ...lines.map(l => l.label)], ...dates.map((d, i) => [label(d), ...lines.map(l => l.points[i])])],
    options: {
      colors: lines.map(l => l.color),
      legend: {
        position: "top"
      },
      vAxis: {
        format: "short"
      },
      pointSize: dates.length > 40 ? 0 : 4,
      hAxis: {
        showTextEvery: Math.max(1, Math.ceil(dates.length / 10))
      },
      tooltip: {
        format: "{0:n0}"
      }
    }
  }));
}